import { useState, useEffect, useCallback } from 'react';
import '../../Styles/ArtistRoot.css';

import { useParams, Link, useNavigate } from 'react-router-dom';
import '../../Styles/Admin.css';
import { BiSolidPlaylist } from 'react-icons/bi';
import { MdDelete, MdAdd, MdClose, MdCloudUpload, MdArrowUpward, MdArrowDownward, MdEdit, MdCheck } from 'react-icons/md';
import izenuAPI from '../../services/izenuAPI';

const COVER_MAX_MB = 10;

const formatDuration = (ms) => {
    if (!ms) return '';
    const total = Math.round(Number(ms) / 1000);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

export default function Playlist() {
    const [playlist, setPlaylist] = useState(null);
    const [musics, setMusics] = useState([]);

    // Edit states
    const [editing, setEditing] = useState(false);
    const [editStatus, setEditStatus] = useState('public');
    const [editTitle, setEditTitle] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [saving, setSaving] = useState(false);

    // Cover upload
    const [coverFile, setCoverFile] = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);
    const [uploadingCover, setUploadingCover] = useState(false);

    // Add track
    const [showAddTrack, setShowAddTrack] = useState(false);
    const [availableTracks, setAvailableTracks] = useState([]);
    const [loadingTracks, setLoadingTracks] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [loadError, setLoadError] = useState(false);
    const [coverError, setCoverError] = useState('');

    let { playlist_id } = useParams();
    const navigate = useNavigate();
    const [deleting, setDeleting] = useState(false);

    const setCover = useCallback((src, size) => {
        const big = size === '100%' || size > 100;
        if (!src || src === "") {
            return (
                <div style={{ width: size, height: size, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: "#e5e7eb" }}>
                    <BiSolidPlaylist size={big ? 56 : 20} color="#9ca3af" />
                </div>
            );
        }
        const types = ['jpg', 'jpeg', 'png', 'webp'];
        const parts = src.split('/');
        const last = parts[parts.length - 1].split('.');
        const ext = last[last.length - 1]?.toLowerCase();
        if (types.indexOf(ext) !== -1) {
            return <img src={src} style={{ width: size, height: size, objectFit: 'cover' }} alt="" />;
        }
        return (
            <div style={{ width: size, height: size, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: "#e5e7eb" }}>
                <BiSolidPlaylist size={big ? 56 : 20} color="#9ca3af" />
            </div>
        );
    }, []);

    const fetchPlaylist = useCallback(async (id) => {
        try {
            const { data: r } = await izenuAPI.get(`/distributor/playlists/listPlaylistMusic.php?playlist_id=${id}`);
            if (r.response) {
                setPlaylist(r.playlist);
                setMusics(r.musics || []);
            } else {
                setLoadError(true);
            }
        } catch (err) {
            console.log(err);
            setLoadError(true);
        }
    }, []);

    const handleStartEdit = useCallback(() => {
        setEditTitle(playlist.title || '');
        setEditDescription(playlist.description || '');
        setEditStatus(playlist.status === 'private' ? 'private' : 'public');
        setEditing(true);
    }, [playlist]);

    const handleSaveEdit = useCallback(async () => {
        setSaving(true);
        try {
            const { data: r } = await izenuAPI.post('/distributor/playlists/updatePlaylistDetails.php', {
                playlist_id: parseInt(playlist_id),
                title: editTitle.trim(),
                description: editDescription.trim(),
                status: editStatus
            });
            if (r.response) {
                setEditing(false);
                fetchPlaylist(playlist_id);
            } else {
                alert('Erro: ' + (r.message || 'Falha ao guardar'));
            }
        } catch (err) {
            console.log(err);
            alert('Erro ao guardar alterações');
        }
        setSaving(false);
    }, [playlist_id, editTitle, editDescription, editStatus, fetchPlaylist]);

    const handleMakePublic = useCallback(async () => {
        setSaving(true);
        try {
            const { data: r } = await izenuAPI.post('/distributor/playlists/updatePlaylistDetails.php', {
                playlist_id: parseInt(playlist_id),
                status: 'public'
            });
            if (r.response) fetchPlaylist(playlist_id);
            else alert('Erro: ' + (r.message || 'Falha ao guardar'));
        } catch (err) {
            console.log(err);
            alert('Erro ao tornar a playlist pública');
        }
        setSaving(false);
    }, [playlist_id, fetchPlaylist]);

    const handleMoveTrack = useCallback(async (index, direction) => {
        const tracks = [...musics];
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= tracks.length) return;

        [tracks[index], tracks[newIndex]] = [tracks[newIndex], tracks[index]];
        const orderedIds = tracks.map(t => t.music_id);

        // Optimistic update
        const updatedTracks = tracks.map((t, i) => ({ ...t, position: i + 1 }));
        setMusics(updatedTracks);

        try {
            const { data: r } = await izenuAPI.post('/distributor/playlists/reorderPlaylistMusic.php', {
                playlist_id: parseInt(playlist_id),
                music_ids: orderedIds
            });
            if (!r.response) {
                fetchPlaylist(playlist_id);
            }
        } catch (err) {
            console.log(err);
            fetchPlaylist(playlist_id);
        }
    }, [musics, playlist_id, fetchPlaylist]);

    const handleRemoveTrack = useCallback(async (musicId, trackTitle) => {
        if (!window.confirm(`Remover "${trackTitle}" desta playlist?`)) return;
        try {
            const { data: r } = await izenuAPI.post('/distributor/playlists/removePlaylistMusic.php', {
                playlist_id: parseInt(playlist_id),
                music_id: musicId
            });
            if (r.response) {
                fetchPlaylist(playlist_id);
            } else {
                alert('Erro: ' + (r.message || 'Falha ao remover'));
            }
        } catch (err) {
            console.log(err);
            alert('Erro ao remover música');
        }
    }, [playlist_id, fetchPlaylist]);

    const fetchAvailableTracks = useCallback(async (search = '') => {
        if (!playlist) return;
        setLoadingTracks(true);
        try {
            let url = `/distributor/playlists/searchTracks.php?exclude_playlist=${playlist_id}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            const { data: r } = await izenuAPI.get(url);
            if (r.response) {
                setAvailableTracks(r.tracks);
            }
        } catch (err) {
            console.log(err);
        }
        setLoadingTracks(false);
    }, [playlist, playlist_id]);

    const handleAddTrack = useCallback(async (musicId) => {
        try {
            const { data: r } = await izenuAPI.post('/distributor/playlists/addPlaylistMusic.php', {
                playlist_id: parseInt(playlist_id),
                music_id: musicId
            });
            if (r.response) {
                fetchPlaylist(playlist_id);
                setAvailableTracks(prev => prev.filter(t => t.music_id !== musicId));
            } else {
                alert('Erro: ' + (r.message || 'Falha ao adicionar'));
            }
        } catch (err) {
            console.log(err);
            alert('Erro ao adicionar música');
        }
    }, [playlist_id, fetchPlaylist]);

    const handleOpenAddTrack = useCallback(() => {
        setShowAddTrack(true);
        setSearchTerm('');
        fetchAvailableTracks();
    }, [fetchAvailableTracks]);

    const handleSearchChange = useCallback((value) => {
        setSearchTerm(value);
        if (value.length >= 2 || value.length === 0) {
            fetchAvailableTracks(value);
        }
    }, [fetchAvailableTracks]);

    const handleCoverChange = useCallback((e) => {
        const file = e.target.files[0];
        e.target.value = '';
        setCoverError('');
        if (!file) return;
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
            setCoverError('Formato não suportado. Usa JPG, PNG ou WEBP.');
            return;
        }
        if (file.size > COVER_MAX_MB * 1024 * 1024) {
            setCoverError(`A imagem tem ${(file.size / 1024 / 1024).toFixed(1)} MB. O máximo é ${COVER_MAX_MB} MB.`);
            return;
        }
        setCoverFile(file);
        setCoverPreview(URL.createObjectURL(file));
    }, []);

    const handleUploadCover = useCallback(async () => {
        if (!coverFile || !playlist) return;
        setUploadingCover(true);
        try {
            const formData = new FormData();
            formData.append('cover', coverFile);
            formData.append('playlist_id', playlist_id);
            const { data: r } = await izenuAPI.post('/distributor/playlists/uploadPlaylistCover.php', formData);
            if (r.response) {
                setCoverFile(null);
                setCoverPreview(null);
                fetchPlaylist(playlist_id);
            } else {
                alert('Erro: ' + (r.message || 'Falha ao carregar capa'));
            }
        } catch (err) {
            console.log(err);
            alert('Erro ao carregar capa');
        }
        setUploadingCover(false);
    }, [coverFile, playlist, playlist_id, fetchPlaylist]);

    const handleDeletePlaylist = useCallback(async () => {
        if (!window.confirm(`Excluir a playlist "${playlist?.title}"?\n\nAs músicas não são apagadas, só a playlist. Quem a tinha guardada deixa de a ver. Esta acção não pode ser desfeita.`)) return;
        setDeleting(true);
        try {
            const { data: r } = await izenuAPI.post('/distributor/playlists/deletePlaylist.php', { playlist_id: parseInt(playlist_id) });
            if (r.response) {
                navigate('/playlists');
                return;
            }
            alert(r.message || 'Não foi possível excluir a playlist.');
        } catch (err) {
            console.log(err);
            alert(err?.response?.data?.message || 'Não foi possível excluir a playlist.');
        }
        setDeleting(false);
    }, [playlist, playlist_id, navigate]);

    useEffect(() => {
        fetchPlaylist(playlist_id);
    }, [playlist_id, fetchPlaylist]);

    if (loadError) {
        return (
            <div className="mx-auto max-w-6xl p-6 text-left">
                <Link to="/playlists" className="text-sm text-amber-700 hover:underline">← Playlists</Link>
                <p className="mt-6 text-gray-600">Não foi possível carregar esta playlist. Pode ter sido apagada, pertencer a outra distribuidora, ou a ligação falhou.</p>
                <button type="button" onClick={() => { setLoadError(false); fetchPlaylist(playlist_id); }}
                    className="mt-3 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium !text-white hover:bg-amber-600">Tentar novamente</button>
            </div>
        );
    }
    if (!playlist) {
        return <p className="p-10 text-center text-sm text-gray-500">A carregar…</p>;
    }

    const isVerified = Number(playlist.verified) === 1;
    const isRejected = !isVerified && !!playlist.verified_at;
    const isPrivate = playlist.status === 'private';
    const verification = isVerified
        ? ['Verificada', 'bg-green-100 text-green-800']
        : isRejected ? ['Rejeitada', 'bg-red-100 text-red-800'] : ['Por verificar', 'bg-amber-100 text-amber-800'];
    const discoveryNote = isPrivate
        ? 'Privada: só o dono a vê. Não aparece na pesquisa nem na descoberta, mesmo verificada.'
        : isVerified
            ? 'Verificada e pública: aparece na pesquisa e na descoberta da app e do site.'
            : isRejected
                ? 'Rejeitada pela Izenu: continua acessível pelo link, mas não aparece na pesquisa nem na descoberta.'
                : 'À espera de verificação pela Izenu. Até lá, só abre por link; não aparece na pesquisa nem na descoberta.';
    const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:ring-amber-500';
    const outlineBtn = 'rounded-lg !border !border-solid border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50';

    return (
        <div className="mx-auto max-w-6xl space-y-6 p-3 sm:p-6 text-left">
            <Link to="/playlists" className="inline-block text-sm text-amber-700 hover:underline">← Playlists</Link>

            {/* Header */}
            <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
                <div className="flex flex-col gap-5 sm:flex-row">
                    <div className="mx-auto w-48 shrink-0 sm:mx-0 sm:w-56">
                        <div className="aspect-square overflow-hidden rounded-xl bg-gray-200">
                            {coverPreview
                                ? <img src={coverPreview} alt="" className="h-full w-full object-cover" />
                                : setCover(playlist.cover, '100%')}
                        </div>
                        {coverPreview ? (
                            <div className="mt-2 flex gap-2">
                                <button type="button" onClick={handleUploadCover} disabled={uploadingCover}
                                    className="flex-1 rounded-lg bg-amber-500 px-3 py-2 text-sm font-medium !text-white hover:bg-amber-600 disabled:opacity-50">
                                    {uploadingCover ? 'A enviar…' : 'Guardar capa'}
                                </button>
                                <button type="button" onClick={() => { setCoverFile(null); setCoverPreview(null); }} disabled={uploadingCover} className={outlineBtn}>Cancelar</button>
                            </div>
                        ) : (
                            <label className={`mt-2 flex cursor-pointer items-center justify-center gap-1.5 ${outlineBtn}`}>
                                <MdCloudUpload size={16} /> {playlist.cover ? 'Alterar capa' : 'Carregar capa'}
                                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleCoverChange} className="sr-only" />
                            </label>
                        )}
                        <ul className="mt-2 space-y-0.5 text-xs [&_li]:!p-0 [&_li]:!text-gray-500">
                            <li><strong className="font-medium text-gray-700">Formato:</strong> JPG, PNG ou WEBP</li>
                            <li><strong className="font-medium text-gray-700">Tamanho ideal:</strong> quadrada, 1200 × 1200 px ou mais</li>
                            <li><strong className="font-medium text-gray-700">Peso máximo:</strong> {COVER_MAX_MB} MB</li>
                        </ul>
                        {coverError && <p className="mt-1 text-xs text-red-600">{coverError}</p>}
                    </div>

                    <div className="min-w-0 flex-1">
                        {editing ? (
                            <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }} className="space-y-4">
                                <label className="block">
                                    <span className="mb-1 block text-sm font-medium text-gray-700">Título</span>
                                    <input className={inputCls} value={editTitle} onChange={(e) => setEditTitle(e.target.value)} required />
                                </label>
                                <label className="block">
                                    <span className="mb-1 block text-sm font-medium text-gray-700">Descrição</span>
                                    <textarea className={`${inputCls} resize-y`} rows={4} value={editDescription} onChange={(e) => setEditDescription(e.target.value)} placeholder="Opcional" />
                                </label>
                                <fieldset>
                                    <legend className="mb-1 block text-sm font-medium text-gray-700">Visibilidade</legend>
                                    <div className="flex flex-col gap-2 sm:flex-row">
                                        {[['public', 'Pública', 'Aparece na pesquisa, na descoberta e na home (se estiver verificada).'],
                                          ['private', 'Privada', 'Só abre para o dono. Fica fora da pesquisa e da home.']].map(([value, label, hint]) => (
                                            <label key={value} className={`flex flex-1 cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 ${editStatus === value ? 'border-amber-500 bg-amber-50' : 'border-gray-300'}`}>
                                                <input type="radio" name="status" value={value} checked={editStatus === value}
                                                    onChange={() => setEditStatus(value)} className="mt-1" />
                                                <span>
                                                    <span className="block text-sm font-medium text-gray-900">{label}</span>
                                                    <span className="block text-xs text-gray-500">{hint}</span>
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </fieldset>
                                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                    <button type="button" onClick={() => setEditing(false)} disabled={saving} className={outlineBtn}>Cancelar</button>
                                    <button type="submit" disabled={saving || !editTitle.trim()}
                                        className="flex items-center justify-center gap-1 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium !text-white hover:bg-amber-600 disabled:opacity-50">
                                        <MdCheck size={16} /> {saving ? 'A guardar…' : 'Guardar alterações'}
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <div className="space-y-3 text-center sm:text-left">
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Playlist</p>
                                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{playlist.title}</h1>
                                </div>
                                <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
                                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${verification[1]}`}>{verification[0]}</span>
                                    {isPrivate && <span className="rounded-full bg-gray-700 px-2.5 py-0.5 text-xs font-semibold text-white">Privada</span>}
                                    <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700">{musics.length} {musics.length === 1 ? 'música' : 'músicas'}</span>
                                </div>
                                {playlist.description && <p className="text-sm text-gray-600 whitespace-pre-line">{playlist.description}</p>}
                                <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600">
                                    {discoveryNote}
                                    {isRejected && playlist.verification_note ? <span className="block text-xs text-gray-500">Motivo: {playlist.verification_note}</span> : null}
                                </p>
                                <button type="button" onClick={handleStartEdit}
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium !text-white hover:bg-amber-600">
                                    <MdEdit size={16} /> Editar
                                </button>
                                {isPrivate && (
                                    <button type="button" onClick={handleMakePublic} disabled={saving}
                                        className="ml-2 inline-flex items-center gap-1.5 rounded-lg !border !border-solid border-green-600 bg-white px-4 py-2 text-sm font-medium text-green-700 hover:bg-green-50 disabled:opacity-50">
                                        <MdCheck size={16} /> {saving ? 'A guardar…' : 'Tornar pública'}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Songs */}
            <section className="rounded-xl border border-gray-200 bg-white">
                <div className="border-b px-4 py-3">
                    <div className="flex items-center gap-2">
                        <h2 className="flex-1 text-lg font-semibold text-gray-900">Músicas</h2>
                        <button type="button" onClick={showAddTrack ? () => { setShowAddTrack(false); setSearchTerm(''); } : handleOpenAddTrack}
                            className="flex items-center gap-1 rounded-lg bg-amber-500 px-3 py-2 text-sm font-medium !text-white hover:bg-amber-600">
                            {showAddTrack ? <><MdClose size={16} /> Fechar</> : <><MdAdd size={16} /> Adicionar música</>}
                        </button>
                    </div>
                    <ul className="mt-2 space-y-0.5 text-xs [&_li]:!p-0 [&_li]:!text-gray-500">
                        <li className="flex items-start gap-1.5"><MdArrowUpward size={14} className="mt-px shrink-0 text-gray-500" aria-hidden="true" />
                            <span>As setas mudam a ordem em que as músicas tocam na playlist.</span></li>
                        <li className="flex items-start gap-1.5"><MdDelete size={14} className="mt-px shrink-0 text-gray-500" aria-hidden="true" />
                            <span><strong className="font-medium text-gray-700">Remover</strong> só tira a música desta playlist. A música continua na conta do artista e no álbum ou single dele, e pode voltar a ser adicionada.</span></li>
                        <li className="flex items-start gap-1.5"><BiSolidPlaylist size={14} className="mt-px shrink-0 text-gray-500" aria-hidden="true" />
                            <span>Podes juntar músicas de qualquer artista da tua distribuidora.</span></li>
                    </ul>
                </div>

                {showAddTrack && (
                    <div className="border-b bg-gray-50 p-4">
                        <div className="search-distributors-input mb-3" style={{ maxWidth: 'none' }}>
                            <input type="search" autoFocus placeholder="Pesquisar músicas dos teus artistas (mín. 2 letras)…" value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)} style={{ padding: '8px 0', fontSize: 14 }} />
                        </div>
                        {loadingTracks ? (
                            <p className="text-sm text-gray-500">A carregar músicas…</p>
                        ) : availableTracks.length === 0 ? (
                            <p className="text-sm text-gray-500">{searchTerm ? 'Nenhuma música encontrada.' : 'Não há mais músicas para adicionar.'}</p>
                        ) : (
                            <ul className="max-h-80 divide-y divide-gray-200 overflow-y-auto rounded-lg border border-gray-200 bg-white [&_li]:!p-0">
                                {availableTracks.map((t) => (
                                    <li key={t.music_id}>
                                        <div className="flex items-center gap-3 px-3 py-2">
                                            <div className="h-9 w-9 shrink-0 overflow-hidden rounded">{setCover(t.cover_url, '100%')}</div>
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-medium text-gray-900">{t.title}</p>
                                                <p className="truncate text-xs text-gray-500">{t.singer}{t.duration ? ` · ${formatDuration(t.duration)}` : ''}</p>
                                            </div>
                                            <button type="button" onClick={() => handleAddTrack(t.music_id)}
                                                className="flex shrink-0 items-center gap-1 rounded-lg bg-amber-500 px-2.5 py-1.5 text-xs font-medium !text-white hover:bg-amber-600">
                                                <MdAdd size={14} /> Adicionar
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {musics.length > 0 ? (
                    <ul className="divide-y divide-gray-100 [&>li]:!p-0">
                        {musics.map((e, i) => (
                            <li key={e.music_id}>
                                <div className="flex items-center gap-2 px-3 py-3 sm:gap-3 sm:px-4">
                                    <div className="flex flex-col">
                                        <button type="button" onClick={() => handleMoveTrack(i, -1)} disabled={i === 0} aria-label="Subir"
                                            className="rounded p-0.5 text-gray-500 hover:bg-gray-100 disabled:text-gray-200"><MdArrowUpward size={14} /></button>
                                        <button type="button" onClick={() => handleMoveTrack(i, 1)} disabled={i === musics.length - 1} aria-label="Descer"
                                            className="rounded p-0.5 text-gray-500 hover:bg-gray-100 disabled:text-gray-200"><MdArrowDownward size={14} /></button>
                                    </div>
                                    <span className="w-5 shrink-0 text-center text-sm text-gray-400">{i + 1}</span>
                                    <div className="h-11 w-11 shrink-0 overflow-hidden rounded">{setCover(e.cover_url || playlist.cover, '100%')}</div>
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm font-medium text-gray-900">
                                            {e.title}
                                            {Number(e.explicit) === 1 && <span title="Conteúdo explícito" style={{ display: 'inline-block', marginLeft: 6, padding: '0 5px', borderRadius: 3, background: '#4b5563', color: '#ffffff', fontSize: 10, fontWeight: 700, lineHeight: '16px', verticalAlign: 'middle' }}>E</span>}
                                        </p>
                                        <p className="truncate text-xs text-gray-500">{e.singer} · {Number(e.plays || 0).toLocaleString('pt-PT')} plays{e.duration ? ` · ${formatDuration(e.duration)}` : ''}</p>
                                    </div>
                                    <button type="button" onClick={() => handleRemoveTrack(e.music_id, e.title)} title="Remover da playlist" aria-label="Remover da playlist"
                                        className="shrink-0 rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"><MdDelete size={18} /></button>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="py-12 text-center text-sm text-gray-500">Esta playlist ainda não tem músicas. Usa "Adicionar música".</p>
                )}
            </section>

            {/* Danger zone */}
            <section className="rounded-xl border border-red-200 bg-red-50/40 p-4 sm:p-6">
                <div className="flex flex-wrap items-center gap-3">
                    <div className="min-w-0 flex-1">
                        <h2 className="text-base font-semibold text-red-800">Excluir playlist</h2>
                        <p className="text-sm text-red-700/80">As músicas não são apagadas, só a playlist. Playlists da Semana e de curadoria activa não podem ser excluídas aqui.</p>
                    </div>
                    <button type="button" onClick={handleDeletePlaylist} disabled={deleting}
                        className="flex items-center gap-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium !text-white hover:bg-red-700 disabled:opacity-50">
                        <MdDelete size={16} /> {deleting ? 'A excluir…' : 'Excluir playlist'}
                    </button>
                </div>
            </section>
        </div>
    );
}
